/**
 * Tiktok 货品外包装枚举（productOuterPackageReq）。
 * 依据：官方样例仅见 packageShape=1|2、packageType=0；
 * 卖家中心选项为：长方体 / 圆柱体 / 其他不规则；硬包装 / 软包装+硬物 / 软包装+软物。
 * 禁止自创取值（如 shape=3、type=4 抽真空）。
 */

import { fixedUi } from '@/i18n/fixedUi';

/** @type {{ value: string, label: string }[]} */
export const TIKTOK_OUTER_PACKAGE_SHAPE_OPTIONS = [
  { value: '1', label: '长方体' },
  { value: '2', label: '圆柱体' },
  { value: '0', label: '其他不规则形状' },
];

/** @type {{ value: string, label: string }[]} */
export const TIKTOK_OUTER_PACKAGE_TYPE_OPTIONS = [
  { value: '0', label: '硬包装' },
  { value: '1', label: '软包装+硬物' },
  { value: '2', label: '软包装+软物' },
];

const SHAPE_SET = new Set([0, 1, 2]);
const TYPE_SET = new Set([0, 1, 2]);

/**
 * 历史错误：把「其他」写成 3（平台拒「不合法的外包装形状」）。
 * @returns {number|null} null=未选
 */
export function normalizeTiktokOuterPackageShape(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n === 3) return 0; // legacy 其他/不规则
  if (!SHAPE_SET.has(n)) return null;
  return n;
}

/**
 * 历史错误：货品外包装误用了 SKU package_type（含抽真空=4，硬包装=3）。
 * 平台外包装仅 0/1/2（样例恒见 packageType:0；卖家中心无抽真空）。
 * 注意：旧 UI 的 1/2 与新枚举语义不同，无法无损迁移——非法值清空，3→0。
 * @returns {number|null}
 */
export function normalizeTiktokOuterPackageType(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n === 4) return null; // 抽真空不属于货品外包装
  if (n === 3) return 0; // 旧「硬包装」→ 平台 0
  if (!TYPE_SET.has(n)) return null;
  return n;
}

/**
 * 组装 productOuterPackageReq。
 * 官方 GLO 服饰样例恒带 { packageShape, packageType }；缺任一会报 Outer packaging incomplete。
 * @param {object} form
 * @param {{ required?: boolean }} [opts] required=true 时未选直接抛错（发品默认）
 * @returns {{ packageShape: number, packageType: number }|null}
 */
export function buildTiktokProductOuterPackageReq(form = {}, opts = {}) {
  const required = opts.required === true;
  const shapeRaw = form.packageShape;
  const typeRaw = form.packageTypeOuter ?? form.outerPackageType;
  const shapeEmpty = shapeRaw === '' || shapeRaw == null;
  const typeEmpty = typeRaw === '' || typeRaw == null;
  if (shapeEmpty && typeEmpty) {
    if (required) {
      throw new Error(fixedUi('请完善货品外包装：选择外包装形状与类型'));
    }
    return null;
  }

  const packageShape = normalizeTiktokOuterPackageShape(shapeRaw);
  let packageType = normalizeTiktokOuterPackageType(typeRaw);

  if (!shapeEmpty && packageShape == null) {
    throw new Error(fixedUi('外包装形状不合法，请选择：长方体 / 圆柱体 / 其他不规则形状'));
  }
  if (!typeEmpty && packageType == null) {
    throw new Error(fixedUi('外包装类型不合法，请选择：硬包装 / 软包装+硬物 / 软包装+软物（不可选抽真空）'));
  }

  // 官方样例：有 shape 时始终带 packageType（常见为 0）
  if (packageShape != null && packageType == null) {
    packageType = 0;
  }
  if (packageType != null && packageShape == null) {
    throw new Error(fixedUi('已选外包装类型时请同时选择外包装形状'));
  }

  return {
    packageShape,
    packageType,
  };
}

/**
 * 组装 productOuterPackageImageReqs（官方服饰样例与 shape/type 同传）。
 * @returns {{ imageUrl: string }[]}
 */
export function buildTiktokProductOuterPackageImageReqs(form = {}) {
  const urls = [];
  const push = (raw) => {
    const s = String(raw || '').trim();
    if (s && !urls.includes(s)) urls.push(s);
  };

  const fromReqs = Array.isArray(form.productOuterPackageImageReqs)
    ? form.productOuterPackageImageReqs
    : [];
  fromReqs.forEach((row) => {
    if (!row) return;
    if (typeof row === 'string') push(row);
    else push(row.imageUrl || row.tiktokUrl);
  });

  const fromImages = Array.isArray(form.outerPackageImages) ? form.outerPackageImages : [];
  fromImages.forEach((img) => {
    if (!img) return;
    if (typeof img === 'string') push(img);
    // 只认 Tiktok 转图结果，避免把站内 IPE 地址签进平台
    else push(img.tiktokUrl);
  });

  const fromUrls = Array.isArray(form.outerPackageImageUrls) ? form.outerPackageImageUrls : [];
  fromUrls.forEach(push);

  return urls.map((imageUrl) => ({ imageUrl }));
}

/** 表单回显：把历史非法值规范成可选 value */
export function coerceOuterPackageFormFields(form = {}) {
  const shape = normalizeTiktokOuterPackageShape(form.packageShape);
  const type = normalizeTiktokOuterPackageType(form.packageTypeOuter ?? form.outerPackageType);
  return {
    packageShape: shape == null ? '' : String(shape),
    packageTypeOuter: type == null ? '' : String(type),
  };
}
